import type { User } from '@core/account-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import type { QuestionKind, QuestionOptions } from '@core/interview-templates-types';
import type { Guest } from '@core/notes-types';
import type { JobItem } from '@core/offers-types';
import { PositionSkill } from '@core/positions-and-skills-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import type { Stage } from '@core/stages-types';
import type { TalentPoolBrief } from '@core/talent-pools-types';

export type Admin = {
  type: string;
  photoThumbUrl: string;
  lastName: string;
  id: number | null;
  firstName: string;
  initials: string;
  hasAvatar: boolean;
  anonymizedAt: string;
  name: string;
};

export enum RatingLevel {
  No = 'no',
  Neutral = 'neutral',
  Yes = 'yes',
  StrongYes = 'strong_yes',
}

export type RatingLevelTranslations = Record<RatingLevel, string>;

export type RatingLevelType = `${RatingLevel}`;

export type Params = {
  candidateId?: number;
  limit?: number;
  page?: number;
  scope?: string;
  adminIds?: number[] | null;
  status?: string;
};
export type InterviewResultAnswerOption = { hint: string; title: string };

export type InterviewResultAnswer = {
  content?: string;
  contentHtml?: string | null;
  contentJson?: EditorJSONState | null;
  note?: string | null;
  noteHtml?: string | null;
  noteJson?: EditorJSONState | null;
  contents?: string[];
  id: number;
  flag?: boolean;
  questionHint: string;
  questionKind: QuestionKind;
  questionRequired: boolean;
  questionTitle: null | string;
  attachmentIds?: number[];
  attachments?: any[];
  options?: InterviewResultAnswerOption[];
  skillRatings: Array<{
    skillId: number;
    proficiencyId: number | null;
  }>;
  questionOptions?: QuestionOptions & { skills?: PositionSkill[] };
};

export type InterviewResult = {
  admin: Admin | User | null;
  adminId: number | null;
  candidate: any;
  candidateId: number;
  createdAt: string;
  guestId: number;
  id: number;
  interviewResultAnswers: InterviewResultAnswer[];
  interviewResultRequestId?: number;
  interviewTemplateName: string;
  interviewTemplateId: string;
  interviewTemplateInfoDescription: string;
  interviewTemplateInfoTitle: string;
  kind: string;
  matchingSkillsCount: number;
  offer?: JobItem | null;
  offerId: number | null;
  talentPool?: TalentPoolBrief | null;
  talentPoolId: number | null;
  rating: RatingLevelType;
  ratingNote: null | string;
  ratingNoteHtml?: null | string;
  ratingNoteJson?: EditorJSONState | null;
  ratingRequired?: boolean;
  ratingScale?: RatingScale;
  score?: number;
  guest?: Guest;
  skillsCount: number;
  stage?: Stage;
  stageId: number | string;
  updatedAt: string;
};

export type HiddenResultPreview = {
  count: number;
  admins: Admin[];
  guests?: Guest[];
  offer: JobItem;
  stage: Stage;
  offerId: number;
  stageId: number | null;
  updatedAt: string;
  guestIds: number[];
  adminIds: number[];
  candidateId: number;
  id: string;
};

export type InterviewResultCollection = {
  requests: InterviewResult[];
  hiddenResultsPreview: HiddenResultPreview[];
  counters: InterviewResultCounters;
};

export enum InterviewGroupBy {
  STATUS = 'status',
  STAGE = 'stage',
  MEMBER = 'member',
  FORM = 'form',
}

export type HiddenInterviewResult = InterviewResult & { hidden?: boolean };

export interface InterviewResultCounters {
  completed: number;
  dismissed: number;
  pending: number;
}

export interface InterviewResultMeta {
  counters: InterviewResultCounters;
}

export interface InterviewResultResponse {
  requests: InterviewResult[];
  hiddenResultsPreview: HiddenResultPreview[];
}

export interface InterviewResultResponseSingle {
  interviewResult: InterviewResult;
}

export interface InterviewResultResponseWithMeta extends InterviewResultResponse {
  meta: InterviewResultMeta;
}

export interface GroupedInterviewResult {
  items: InterviewResult[];
  title: string | null;
  titleTranslationKey?: string;
  rating: number | null;
  offer?: JobItem | null;
  position?: string;
  hiddenResultsPreview?: HiddenResultPreview[];
}
